import React from "react";
import { useLayoutEffect } from "react";
import MUIDataTable from "mui-datatables";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import LinearProgress from "@material-ui/core/LinearProgress";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import AddIcon from "@material-ui/icons/Add";
import Alert from "@material-ui/lab/Alert";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { BASE_URL } from "../utils/index";
import { postJson } from "../utils/postJson";
import { showError, showSuccess } from "../utils/funcionesSwal";
import { fetchViajesProgramados } from "../api/viajes";
import {
  cerrarSwalTramitando,
  mostrarSwalTramitando,
} from "../utils/funcionesSwal";

const TIPOS_QUEJA = ["Queja de servicio", "Error del sistema", "Sugerencia", "Otro"];

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString("es-ES")} ${date.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

const useStyles = makeStyles((theme) => ({
  layout: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    top: 64,
    right: 0,
    bottom: 0,
    left: 240,
    minWidth: 0,
    overflow: "hidden",
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down("sm")]: {
      left: 0,
    },
    [theme.breakpoints.down("xs")]: {
      top: 56,
      left: 0,
      padding: theme.spacing(2),
    },
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexShrink: 0,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
    },
  },
  heading: {
    minWidth: 0,
  },
  title: {
    marginBottom: theme.spacing(0.5),
  },
  intro: {
    maxWidth: 560,
  },
  newComplaintButton: {
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  progress: {
    marginBottom: theme.spacing(1),
    flexShrink: 0,
  },
  table: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "100%",
    width: "100%",
    minWidth: 0,
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden",
    "& > .MuiPaper-root": {
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      flex: "0 1 auto",
      minHeight: 0,
      maxHeight: "100%",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.spacing(1),
      boxShadow: theme.shadows[1],
      overflow: "hidden",
    },
    "& > .MuiPaper-root > :not([class*='responsiveBase'])": {
      flexShrink: 0,
    },
    "& > .MuiPaper-root > [class*='responsiveBase']": {
      flex: "1 1 auto",
      minHeight: 0,
      overflow: "auto",
    },
    "& .MuiToolbar-root": {
      minHeight: 56,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.5, 1),
    },
    "& .MuiTableHead-root .MuiTableCell-head": {
      backgroundColor: "#f4f6fa",
      color: theme.palette.text.secondary,
      fontSize: "0.72rem",
      fontWeight: 700,
      letterSpacing: "0.035em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    },
    "& .MuiTableBody-root .MuiTableCell-body": {
      padding: theme.spacing(1.25, 1),
      borderBottom: `1px solid ${theme.palette.divider}`,
      verticalAlign: "middle",
    },
    "& .MuiTablePagination-root": {
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.grey[50],
      flexShrink: 0,
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: "rgba(63, 81, 181, 0.045)",
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "background-color 120ms ease-in-out",
    },
    [theme.breakpoints.down("sm")]: {
      "& .MuiTableBody-root .MuiTableCell-body": {
        display: "grid",
        gridTemplateColumns: "96px minmax(0, 1fr)",
        alignItems: "start",
        gap: theme.spacing(1),
        width: "100% !important",
        minWidth: "0 !important",
        whiteSpace: "normal !important",
        textAlign: "left !important",
      },
      "& .MuiTableBody-root .MuiTableCell-body > div": {
        display: "block",
        width: "auto",
        minWidth: 0,
        fontSize: "0.875rem",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
      },
      "& .MuiTableBody-root .MuiTableCell-body > div:first-child": {
        color: theme.palette.text.secondary,
        fontWeight: 500,
      },
      "& .MuiTableFooter-root .MuiTableCell-footer": {
        padding: 0,
      },
      "& .MuiTablePagination-root": {
        width: "100%",
        minWidth: 0,
      },
      "& .MuiTablePagination-toolbar": {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        columnGap: theme.spacing(1),
        rowGap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1.5),
      },
      "& .MuiTablePagination-spacer": {
        display: "none",
      },
      "& .MuiTablePagination-selectRoot": {
        justifySelf: "end",
        margin: 0,
      },
      "& .MuiTablePagination-actions": {
        marginLeft: 0,
      },
    },
  },
}));

async function fetchQuejas() {
  let dataQuejas;
  let dataSoporte;
  try {
    const idCliente = localStorage.getItem("idCliente");
    [dataQuejas, dataSoporte] = await Promise.all([
      postJson(`${BASE_URL}/quejasEnviadas`, { idCliente }),
      postJson(`${BASE_URL}/soportesEnviados`, { idCliente }),
    ]);
  } catch (error) {
    if (error.status === 401) {
      throw new Error("La sesión no es válida. Inicia sesión de nuevo.");
    }
    if (error.status) {
      throw new Error(
        `No se pudieron cargar las quejas (HTTP ${error.status}). Puedes reintentar la consulta.`
      );
    }
    throw new Error("No se pudo conectar con el servidor para cargar las quejas.");
  }

  if (
    !dataQuejas ||
    !Array.isArray(dataQuejas.quejas) ||
    !dataSoporte ||
    !Array.isArray(dataSoporte.soportes)
  ) {
    throw new Error("El servidor devolvió una respuesta de quejas inválida.");
  }

  const quejas = dataQuejas.quejas.map((queja) => ({
    ...queja,
    numeroRegistro: `Q-${queja.id}`,
    descripcion: queja.descripcion,
    respuesta: queja.mensaje,
    estado: queja.estatus,
  }));
  const solicitudesSoporte = dataSoporte.soportes.map((soporte) => ({
    ...soporte,
    numeroRegistro: `S-${soporte.id}`,
    idViaje: null,
    descripcion: soporte.mensaje,
    respuesta: "—",
    estado: "Enviada a soporte",
    nombreUsuario: "—",
  }));

  return [...quejas, ...solicitudesSoporte].sort((a, b) => {
    const fechaA = new Date(a.created_at).getTime() || 0;
    const fechaB = new Date(b.created_at).getTime() || 0;
    return fechaB - fechaA;
  });
}

const columnDefinitions = [
  { name: "numeroRegistro", label: "Número", sort: false },
  {
    name: "created_at",
    label: "Fecha de envío",
    options: {
      sort: false,
      customBodyRender: formatDateTime,
    },
  },
  { name: "tipo", label: "Tipo" },
  {
    name: "idViaje",
    label: "Viaje",
    options: { customBodyRender: (value) => value || "—" },
  },
  { name: "descripcion", label: "Queja" },
  { name: "respuesta", label: "Respuesta" },
  { name: "estado", label: "Estado" },
  { name: "nombreUsuario", label: "Atendida por" },
];

const options = {
  selectableRows: "none",
  print: false,
  responsive: "vertical",
  rowsPerPage: 25,
  rowsPerPageOptions: [10, 25, 50, 100],
  setTableProps: () => ({ stickyHeader: true, size: "small" }),
  downloadOptions: {
    filename: "QuejasEnviadasTaxisLleida.csv",
  },
  textLabels: {
    body: { noMatch: "No se han enviado quejas" },
    pagination: {
      next: "Página siguiente",
      previous: "Página anterior",
      rowsPerPage: "Filas por página:",
      displayRows: "de",
    },
    toolbar: {
      search: "Buscar quejas",
      filterTable: "Filtrar quejas",
      viewColumns: "Ver columnas",
      downloadCsv: "Descargar CSV",
    },
  },
};

export default function Quejas() {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = React.useState(false);
  const [formData, setFormData] = React.useState({
    nombre: "",
    tipo: "",
    idViaje: "",
    descripcion: "",
  });
  const {
    data,
    isLoading,
    error,
    isFetching,
    isFetchedAfterMount,
    refetch,
  } = useQuery(
    "quejasEnviadas",
    fetchQuejas,
    { retry: false, refetchOnMount: "always", refetchOnWindowFocus: false }
  );
  const [initialViewLoaded, setInitialViewLoaded] = React.useState(false);
  const initialViewLoadingRef = React.useRef(true);

  useLayoutEffect(() => {
    mostrarSwalTramitando();

    return () => {
      if (initialViewLoadingRef.current) {
        initialViewLoadingRef.current = false;
        cerrarSwalTramitando();
      }
    };
  }, []);

  React.useEffect(() => {
    if (
      initialViewLoadingRef.current &&
      isFetchedAfterMount &&
      !isFetching
    ) {
      initialViewLoadingRef.current = false;
      setInitialViewLoaded(true);
      cerrarSwalTramitando();
    }
  }, [isFetchedAfterMount, isFetching]);

  const hideEmptyHistoryWhileFetching =
    !initialViewLoaded || (isFetching && !(data?.length > 0));
  const viajesQuery = useQuery(
    "viajesParaNuevaQueja",
    () =>
      fetchViajesProgramados({
        page: 1,
        perPage: 100,
        sortBy: "idVP",
        sortDirection: "desc",
      }),
    { enabled: formOpen, retry: false, refetchOnWindowFocus: false }
  );
  const sendComplaint = useMutation(
    async (complaint) => {
      const response = await postJson(`${BASE_URL}/soporte`, complaint);
      if (response.message !== "Success!") {
        throw new Error(response.estatus || response.message || "No se pudo enviar la queja.");
      }
      return response;
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries("quejasEnviadas");
        setFormOpen(false);
        setFormData({ nombre: "", tipo: "", idViaje: "", descripcion: "" });
        showSuccess(response.estatus || "Tu queja se ha enviado correctamente.");
      },
      onError: (error) => showError(error.message),
    }
  );
  const columns = columnDefinitions.map(({ name, label, sort, options: columnOptions }) => ({
    name,
    label,
    options: {
      filter: true,
      sort: Boolean(sort),
      ...columnOptions,
    },
  }));
  const selectedTrip = (viajesQuery.data?.viajes || []).find(
    (trip) => String(trip.idVP) === formData.idViaje
  );
  const tripPrefix = selectedTrip
    ? `Viaje #${selectedTrip.idVP} · ${selectedTrip.servicio} · ${selectedTrip.fechaInicio}: `
    : "";

  return (
    <main className={classes.layout}>
      <div className={classes.pageHeader}>
        <div className={classes.heading}>
          <Typography component="h1" variant="h4" className={classes.title}>
            Quejas
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.intro}>
            Consulta tus quejas anteriores y las solicitudes enviadas a soporte.
          </Typography>
        </div>
        <Button
          className={classes.newComplaintButton}
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            sendComplaint.reset();
            setFormData({ nombre: "", tipo: "", idViaje: "", descripcion: "" });
            setFormOpen(true);
          }}
        >
          Nueva queja
        </Button>
      </div>
      {(isLoading || isFetching) && (
        <LinearProgress
          aria-label="Cargando quejas"
          className={classes.progress}
        />
      )}
      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        >
          {error.message}
        </Alert>
      ) : (
        !isLoading && !hideEmptyHistoryWhileFetching && data && (
          <div className={classes.table}>
            <MUIDataTable
              title="Historial de quejas"
              data={data}
              columns={columns}
              options={options}
            />
          </div>
        )
      )}
      <Dialog
        open={formOpen}
        onClose={() => !sendComplaint.isLoading && setFormOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="nueva-queja-title"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            sendComplaint.mutate({
              nombre: formData.nombre.trim(),
              tipo: formData.tipo,
              mensaje: `${tripPrefix}${formData.descripcion.trim()}`,
            });
          }}
        >
          <DialogTitle id="nueva-queja-title">Nueva queja</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="textSecondary" className={classes.formField}>
              La solicitud se guardará en el apartado de soporte con el correo de tu cuenta.
            </Typography>
            <TextField
              className={classes.formField}
              required
              fullWidth
              variant="outlined"
              label="Nombre y apellidos"
              value={formData.nombre}
              inputProps={{ maxLength: 255 }}
              onChange={(event) =>
                setFormData((current) => ({ ...current, nombre: event.target.value }))
              }
            />
            <TextField
              className={classes.formField}
              select
              required
              fullWidth
              variant="outlined"
              label="Tipo de queja"
              value={formData.tipo}
              onChange={(event) =>
                setFormData((current) => ({ ...current, tipo: event.target.value }))
              }
            >
              {TIPOS_QUEJA.map((tipo) => (
                <MenuItem key={tipo} value={tipo}>
                  {tipo}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              className={classes.formField}
              select
              fullWidth
              variant="outlined"
              label="Viaje relacionado (opcional)"
              value={formData.idViaje}
              onChange={(event) =>
                setFormData((current) => ({ ...current, idViaje: event.target.value }))
              }
              helperText={
                viajesQuery.isLoading
                  ? "Cargando viajes recientes…"
                  : viajesQuery.error
                    ? "No se pudieron cargar los viajes; puedes continuar sin asociar uno."
                    : "Opcional. Se añadirá como referencia al mensaje."
              }
            >
              <MenuItem value="">Sin asociar un viaje</MenuItem>
              {(viajesQuery.data?.viajes || []).map((viaje) => (
                <MenuItem key={viaje.idVP} value={String(viaje.idVP)}>
                  {`#${viaje.idVP} · ${viaje.servicio || "Viaje"} · ${viaje.fechaInicio}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              className={classes.formField}
              required
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              label="Describe lo ocurrido"
              value={formData.descripcion}
              inputProps={{ maxLength: Math.max(1, 255 - tripPrefix.length) }}
              onChange={(event) =>
                setFormData((current) => ({ ...current, descripcion: event.target.value }))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setFormOpen(false)}
              disabled={sendComplaint.isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={sendComplaint.isLoading}
            >
              {sendComplaint.isLoading ? "Enviando…" : "Enviar queja"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </main>
  );
}
